import { createSelector } from 'reselect'
import { sortByAll } from 'lib/array/arrayUtils'
import getCreditPayableAmount from 'checkout/selectors/payment/getCreditPayableAmount'

const getCheckoutLoyaltyUnearnableCreditAmount = createSelector(
  (state: App.State) => getCreditPayableAmount(state),
  (state: App.State) => state.auth.account.creditsCollection,
  (creditPayableAmount, creditsCollection) => {
    if (!creditPayableAmount || !creditsCollection?.data?.length) return 0

    let remainingCreditAmount = creditPayableAmount
    let unearnedCreditAmount = 0

    /**
     * Mirrors the backend spend order (`compareCreditBalances` in svc-payment):
     * earliest expiry first, then earliest creation, then credit id.
     *
     * Sorting on `expiresAt` alone is not enough. Refund up/downgrades issue an
     * unearnable and an earnable credit milliseconds apart sharing one expiry,
     * and svc-payment truncates the milliseconds off `expiresAt` — so those
     * credits arrive with identical expiry strings. Without the extra keys their
     * order would depend on how the API happened to return them, and an earnable
     * credit could consume the payable amount ahead of an unearnable one that
     * the backend actually spends first.
     *
     * `creditId` is the last resort. svc-payment compares it as a plain string
     * (`a.id_credit < b.id_credit`), which is the same comparison applied here.
     */
    const sortedCreditsCollectionByExpiryDate = sortByAll(creditsCollection.data, [
      { selector: (credit) => new Date(credit.expiresAt).getTime(), direction: 'asc' },
      { selector: (credit) => new Date(credit.createdAt).getTime(), direction: 'asc' },
      { selector: (credit) => credit.creditId, direction: 'asc' },
    ])

    sortedCreditsCollectionByExpiryDate.forEach((credit) => {
      if (!credit.isLoyaltyEarnable) {
        const amountToAdd = Math.min(remainingCreditAmount, credit.amount)
        unearnedCreditAmount += amountToAdd
      }

      const amountToSubtract = Math.min(remainingCreditAmount, credit.amount)
      remainingCreditAmount -= amountToSubtract
    })

    return unearnedCreditAmount
  },
)

export default getCheckoutLoyaltyUnearnableCreditAmount
